import { notEmpty } from "@orb-labs/types";
/**
 * Default sensitive patterns to filter from error messages
 */
const DEFAULT_SENSITIVE_PATTERNS = [
    /api[_-]?key/i,
    /secret/i,
    /password/i,
    /token/i,
    /private/i,
    /mnemonic/i,
    /seed/i,
    /credential/i,
    /bearer/i,
    /authorization/i,
];
/**
 * ErrorSanitizer - Prevents sensitive information leakage in error messages
 *
 * Features:
 * - Filters error messages containing sensitive keywords
 * - Configurable sensitive patterns
 * - Length limiting to prevent verbose internal errors
 * - Safe fallback messages
 * - Deep sanitization for monitoring payloads (Sentry, etc.)
 */
export class ErrorSanitizer {
    constructor(config = {}) {
        this.sensitivePatterns = config.sensitivePatterns ?? DEFAULT_SENSITIVE_PATTERNS;
        this.maxMessageLength = config.maxMessageLength ?? 200;
    }
    /**
     * Sanitize an error message for safe display to users
     */
    sanitize(error, fallbackMessage) {
        const errorMessage = this.extractMessage(error);
        if (this.containsSensitiveContent(errorMessage)) {
            return fallbackMessage;
        }
        if (errorMessage.length > this.maxMessageLength) {
            return fallbackMessage;
        }
        if (errorMessage.length > 0) {
            return errorMessage;
        }
        return fallbackMessage;
    }
    /**
     * Check if a message contains sensitive content
     */
    containsSensitiveContent(message) {
        for (const pattern of this.sensitivePatterns) {
            if (pattern.test(message)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Recursively redact sensitive strings and keys in arbitrary JSON-like values (e.g. Sentry extra, breadcrumbs).
     */
    sanitizeValue(value) {
        if (!notEmpty(value)) {
            return value;
        }
        if (typeof value === "string") {
            return this.redact(value);
        }
        if (Array.isArray(value)) {
            return value.map((item) => this.sanitizeValue(item));
        }
        if (typeof value === "object") {
            const sanitized = {};
            for (const [key, v] of Object.entries(value)) {
                if (this.containsSensitiveContent(key)) {
                    sanitized[key] = "[REDACTED]";
                }
                else {
                    sanitized[key] = this.sanitizeValue(v);
                }
            }
            return sanitized;
        }
        return value;
    }
    /**
     * Redact sensitive values from a string (for logging / telemetry)
     */
    redact(text) {
        let result = text;
        result = result.replace(/(token|key|secret|password|credential)s?[:\s]*["']?[^,\s"'}]*/gi, "$1=[REDACTED]");
        result = result.replace(/Bearer\s+[^\s"']+/gi, "Bearer [REDACTED]");
        result = result.replace(/\b([a-z]+\s+){11,23}[a-z]+\b/gi, "[MNEMONIC REDACTED]");
        return result;
    }
    extractMessage(error) {
        if (typeof error === "string") {
            return error;
        }
        if (notEmpty(error) && typeof error === "object" && "message" in error) {
            const msg = error.message;
            // Match legacy `if (error?.message)` — skip empty string and other falsy messages
            if (notEmpty(msg) && Boolean(msg)) {
                return String(msg);
            }
        }
        if (notEmpty(error) &&
            typeof error === "object" &&
            "toString" in error &&
            typeof error.toString === "function") {
            const str = error.toString();
            if (str !== "[object Object]") {
                return str;
            }
        }
        return "";
    }
}
/** Default singleton instance */
export const errorSanitizer = new ErrorSanitizer();
export const sanitizeErrorMessage = (error, fallbackMessage) => errorSanitizer.sanitize(error, fallbackMessage);
